import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ToolCard } from "@/components/tools/ToolCard";
import { TOOLS, TOOL_CATEGORIES } from "@/constants/tools";
import { Container } from "@/components/Layout/Container";
import { captureEvent, EVENTS } from '@/lib/posthog';

export function ToolOverview() {
  const navigate = useNavigate();

  // Memoize grouped tools to prevent recalculation on every render
  const groupedTools = useMemo(() =>
    TOOL_CATEGORIES.reduce((acc, category) => ({
      ...acc,
      [category.id]: TOOLS.filter(tool => tool.category === category.id)
    }), {} as Record<string, typeof TOOLS>),
  []);

  // Memoize navigation handler with analytics tracking
  const handleLaunch = useCallback(
    (path: string) => {
      const tool = TOOLS.find(t => t.path === path);
      if (tool) {
        captureEvent(EVENTS.TOOL_LAUNCHED, {
          tool_id: tool.id,
          tool_name: tool.title,
        });
      }
      navigate(path);
    },
    [navigate]
  );

  return (
    <Container maxWidth="2xl" className="px-4 py-8">
      <PageHeader />

      {TOOL_CATEGORIES.map((category) => (
        <ToolCategorySection
          key={category.id}
          category={category}
          tools={groupedTools[category.id]}
          onLaunch={handleLaunch}
        />
      ))}
    </Container>
  );
}

// Extracted components for better readability and potential reuse
const PageHeader = () => (
  <header className="mb-10">
    <h1 className="text-3xl font-bold mb-3 tracking-tight">
      Quantity Surveyor Tools
    </h1>
    <p className="text-muted-foreground text-lg">
      Access our suite of specialised tools designed to help you manage your projects effectively.
    </p>
  </header>
);

type ToolCategorySectionProps = {
  category: typeof TOOL_CATEGORIES[number];
  tools: typeof TOOLS;
  onLaunch: (path: string) => void;
};

const ToolCategorySection = ({ category, tools, onLaunch }: ToolCategorySectionProps) => {
  const availableCount = tools.filter(t => !t.comingSoon).length;

  return (
    <section className="mb-12 pb-8 border-b border-border/50 last:border-0">
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h2 className="text-xl font-semibold mb-1">{category.name}</h2>
          <p className="text-sm text-muted-foreground">{category.description}</p>
        </div>
        <span className="text-sm text-muted-foreground">
          {availableCount}/{tools.length} available
        </span>
      </header>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {tools.map((tool) => (
          <ToolCard
            key={tool.id}
            tool={tool}
            onLaunch={onLaunch}
          />
        ))}
      </div>
    </section>
  );
};